import React, { useState, useEffect } from 'react';
import { FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ContractorAuthenticationModal from '../../../components/family-plaza/ContractorAuthenticationModal';
import PortraitPhotoRegistrationModal from '../../../components/family-plaza/PortraitPhotoRegistrationModal';
import { checkModalAuthenticationStatus } from '../../../utils/authUtils';
import { getCurrentUserProfilePhoto, getCurrentCustomerName, setupProfilePhotoListener, getDeceasedPersonDate, getDeceasedPersonProfileText } from '../../../utils/profilePhotoUtils';
import CyberMemorialService from '../../../services/FamilyPlaza/CyberMemorialService';
import FamilyPlazaTopSection from '../../../components/family-plaza/FamilyPlazaTopSection';
import FamilyPlazaSubHeading from '../../../components/family-plaza/FamilyPlazaSubHeading';
import DeceasedPersonProfile from '../../../components/family-plaza/DeceasedPersonProfile';

const MemorialAlbumBoardListMobile = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [memorialAlbums, setMemorialAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('/mobile donate/group-129.png');
  const [displayedAlbums, setDisplayedAlbums] = useState([]);
  const [hasMoreAlbums, setHasMoreAlbums] = useState(false);
  const [albumsPerPage] = useState(5);
  const [allAlbums, setAllAlbums] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const handleMemorialPost = () => {
    navigate('/cyber-memorial-search');
  };

  const handleWriteAlbum = () => {
    if (checkModalAuthenticationStatus()) {
      navigate('/memorial-album-writing');
    } else {
      setShowAuthModal(true);
    }
  };

  const handleWriteButton = () => {
    navigate('/memorial-album-writing');
  };

  // Photo icon click handler
  const handlePhotoIconClick = () => {
    if (checkModalAuthenticationStatus()) {
      setShowPhotoModal(true);
    } else {
      setShowAuthModal(true);
    }
  };

  // Authentication modal handlers
  const handleAuthConfirm = () => {
    setShowAuthModal(false);
    navigate('/memorial-album-writing');
  };

  const handleAuthClose = () => {
    setShowAuthModal(false);
  };

  // Photo registration modal handlers
  const handlePhotoConfirm = () => {
    setShowPhotoModal(false);
  };

  const handlePhotoUpload = (photoUrl) => {
    setProfilePhotoUrl(photoUrl);
  };

  const handlePhotoClose = () => {
    setShowPhotoModal(false);
  };

  const handleLetterToHeaven = () => {
    navigate('/letter-to-heaven-board-list');
  };

  const handleManagementInquiry = () => {
    navigate('/management-search');
  };

  const handleAncestralTablet = () => {
    navigate('/ancestral-tablet');
  };

  // Handle load more albums
  const handleLoadMore = async () => {
    if (isLoading || currentPage >= Math.ceil(allAlbums.length / albumsPerPage)) return;
    
    setIsLoading(true);
    try {
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * albumsPerPage;
      const endIndex = startIndex + albumsPerPage;
      
      // Get next batch of albums from allAlbums
      const nextBatch = allAlbums.slice(startIndex, endIndex);
      
      if (nextBatch.length > 0) {
        setDisplayedAlbums(prevAlbums => [...prevAlbums, ...nextBatch]);
        setCurrentPage(nextPage);
        setHasMoreAlbums(endIndex < allAlbums.length);
      }
    } catch (error) {
      console.error('Error loading more albums:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle album click - navigate to article details
  const handleAlbumClick = async (album) => {
    try {
      // Fetch detailed album data from API
      const response = await CyberMemorialService.getMemorialAlbumDetail(album.albumId);
      
      if (response) {
        const idsInView = displayedAlbums.map(a => a.albumId);
       
        // Navigate to the article details page with detailed album data from API
        navigate('/memorial-album-article-details', { 
          state: { 
            albumData: {
              albumId: response.albumId || album.albumId,
              title: response.albumTitle || album.title,
              date: formatDate(response.registTime || album.registTime),
              image: response.photoUrl || response.photoThumbUrl || album.thumbUrl || album.photoUrl,
              readCount: response.readCount,
              customerName: response.customerName || album.customerName,
              photoThumbUrl: response.photoThumbUrl,
              registTime: response.registTime,
              cherishId: response.cherishId,
              goinId: response.goinId
            }, 
            sortedAlbumIds: idsInView
          }
        });
      } 
     
    } catch (error) {
      console.error('Error fetching album details:', error);
      
      // Fallback navigation with basic album data if API call fails
      navigate('/memorial-album-article-details', { 
        state: { 
          albumData: {
            albumId: album.albumId,
            title: album.title,
            date: formatDate(album.registTime),
            image: album.thumbUrl || album.photoUrl,
            readCount: album.readCount,
            customerName: album.customerName,
            photoThumbUrl: album.thumbUrl,
            registTime: album.registTime
          }
        }
      });
    }
  };

  // Load memorial albums via CyberMemorialService
  useEffect(() => {
    const loadAlbums = async () => {
      try {
        const selectedPersonData = localStorage.getItem('selectedPersonData');
        const personData = selectedPersonData ? JSON.parse(selectedPersonData) : null;
        const goinId = personData?.goinId;
        if (!goinId) {
          setMemorialAlbums([]);
          setDisplayedAlbums([]);
          setHasMoreAlbums(false);
          setLoading(false);
          return;
        }

        setLoading(true);
        const response = await CyberMemorialService.getMemorialAlbumList(goinId, 1, 200);
        const rawAlbums = Array.isArray(response?.albums) ? response.albums : [];
        const mapped = rawAlbums.map(a => ({
          albumId: a.albumId,
          title: a.albumTitle || '제목 없음',
          thumbUrl: a.photoThumbUrl || a.photoUrl,
          photoUrl: a.photoUrl,
          readCount: a.readCount || 0,
          registTime: a.registTime || new Date().toISOString(),
          customerName: a.customerName || 'default'
        }));
        const sorted = mapped.sort((a, b) => new Date(b.registTime) - new Date(a.registTime));

        setAllAlbums(sorted);
        setMemorialAlbums(sorted);
        const firstPage = sorted.slice(0, albumsPerPage);
        setDisplayedAlbums(firstPage);
        setCurrentPage(1);
        setHasMoreAlbums(sorted.length > albumsPerPage);
      } catch (e) {
        setMemorialAlbums([]);
        setDisplayedAlbums([]);
        setHasMoreAlbums(false);
      } finally {
        setLoading(false);
      }
    };
    loadAlbums();
  }, [albumsPerPage]);

  

  // Set initial displayed albums and hasMoreAlbums when memorialAlbums change (kept for safety)
  useEffect(() => {
    const initialAlbums = memorialAlbums.slice(0, albumsPerPage);
    setDisplayedAlbums(initialAlbums);
    setHasMoreAlbums(memorialAlbums.length > albumsPerPage);
  }, [memorialAlbums, albumsPerPage]);

  // Set up view count listener for real-time synchronization
  useEffect(() => {
    // This useEffect is no longer needed as view counts are managed by API
  }, [memorialAlbums]);

  return (
    <div className="memorial-album-board-list-mobile-unique announcement-list-page cyber-info-page-pc">
      
      <FamilyPlazaTopSection />
  
     <main className="main-content">
  
      <FamilyPlazaSubHeading heading="사이버추모관" />


      <DeceasedPersonProfile 
        handlePhotoIconClick={handlePhotoIconClick}
      />
     

      {/* Navigation Tabs */}
      <div className="memorial-album-board-list-mobile-nav-tabs-unique">
        <button 
          className="memorial-album-board-list-mobile-nav-tab-unique"
          onClick={() => navigate('/donate')}
        >
          <span>헌화하기</span>
        </button>
        <button 
          className="memorial-album-board-list-mobile-nav-tab-unique"
          onClick={() => navigate('/memorial-post-board-list')}
        >
          <span>추모글</span>
        </button>
        <button 
          className="memorial-album-board-list-mobile-nav-tab-unique memorial-album-board-list-mobile-nav-tab-active-unique"
          onClick={() => navigate('/memorial-album-board-list')}
        >
          <span>추모앨범</span>
        </button>
      </div>

        {/* Write Album Button */}
        <div className="memorial-album-board-list-mobile-button-section-unique">
          <button 
            className="memorial-album-board-list-mobile-write-button-unique"
            onClick={handleWriteAlbum}
          >
            글쓰기
          </button>
        </div>

             {/* Memorial Album Content Cards */}
       <div className="memorial-album-board-list-mobile-content-section-unique">
         {loading ? (
           <div>Loading albums...</div>
         ) : displayedAlbums.length > 0 ? (
           displayedAlbums.map((album) => (
             <div 
               key={album.albumId} 
               className="memorial-album-board-list-mobile-album-card-unique"
               onClick={() => handleAlbumClick(album)}
               style={{ cursor: 'pointer' }}
             >
               <div className="memorial-album-board-list-mobile-album-image-unique">
                 <img 
                  src={album.thumbUrl || album.photoUrl} 
                  alt={album.title}
                />
               </div>
               <div className="memorial-album-board-list-mobile-album-info-unique">
                 <div className="memorial-album-board-list-mobile-album-title-unique">
                   {album.title}
                 </div>
                 <div className="memorial-album-board-list-mobile-album-meta-unique">
                   <span className="memorial-album-board-list-mobile-album-date-unique">
                     {formatDate(album.registTime)}
                   </span>
                   <div className="memorial-album-board-list-mobile-album-views-unique">
                     <FaEye />
                     <span>{album.readCount}</span>
                   </div>
                 </div>
               </div>
             </div>
           ))
         ) : (
           <div>No albums available</div>
         )}

         {/* More Button */}
         {hasMoreAlbums && (
           <div className="memorial-album-board-list-mobile-load-more-section-unique">
             <button 
               onClick={handleLoadMore} 
               className="memorial-album-board-list-mobile-load-more-btn-unique"
               disabled={isLoading}
             >
               {isLoading ? '로딩 중...' : '더보기'}
             </button>
           </div>
         )}

      
      </div>

      {/* Authentication Modal */}
      <ContractorAuthenticationModal
        isOpen={showAuthModal}
        onConfirm={handleAuthConfirm}
        onClose={handleAuthClose}
      />

      {/* Portrait Photo Registration Modal */}
      <PortraitPhotoRegistrationModal
        isOpen={showPhotoModal}
        onConfirm={handlePhotoConfirm}
        onClose={handlePhotoClose}
        onPhotoUpload={handlePhotoUpload}
      />
      </main>
    </div>
  );
};

export default MemorialAlbumBoardListMobile; 